import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';
import { ProductRewardsResponseDTO } from 'src/app/shared/models/dtos/responses/product-rewards-response-dto/ProductRewardsResponseDto';
import { RoutesKeys } from 'src/app/utils/routes';

interface DisplayReward extends ProductRewardsResponseDTO {
  displayName: string;
  displayDiscount: string;
}

@Component({
  selector: 'app-product-rewards',
  templateUrl: './product-rewards.component.html',
  styleUrls: ['./product-rewards.component.scss'],
})
/**
 * Product Rewards list.
 */
export class ProductRewardsComponent implements OnInit {
  @Input() product!: ProductDTO;
  @Output() addReward: EventEmitter<void> = new EventEmitter<void>();
  @Output() deleteReward: EventEmitter<ProductRewardsResponseDTO> =
    new EventEmitter<ProductRewardsResponseDTO>();

  call?: Subscription;
  rewards: DisplayReward[] = [];
  activeLabel = 'active';
  addRewardLabel = 'add-reward';
  exceptionsLabel = 'exceptions';
  inactiveLabel = 'inactive';
  language = '';
  loading = false;
  page = 0;
  pageSize = 5;
  pulling = false;
  labels = {
    error: 'unexpected-error',
  };

  /**
   * Constructor
   */
  constructor(
    private readonly router: Router,
    private readonly rewardService: RewardService,
    private readonly snackBarService: SnackBarService,
    private readonly translationService: TranslationService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.language = this.translationService.getActiveLang();
    this.getRewards();
  }

  /**
   * redirects the user to the add rewards screen
   * @param {ProductRewardsResponseDTO} reward - The reward to edit.
   */
  goToRewardEdit(reward?: ProductRewardsResponseDTO) {
    if (reward) {
      this.router.navigate([RoutesKeys.REWARD_DETAIL, reward.id], {
        state: { rewardData: reward },
      });
    } else {
      this.router.navigate([RoutesKeys.REWARD_DETAIL], {
        state: { product: this.product },
      });
    }
    this.addReward.emit();
  }

  /**
   * Get rewards by product id
   */
  getRewards() {
    this.loading = true;
    if (this.call) {
      this.call.unsubscribe();
    }
    this.page = 0;
    this.call = this.rewardService
      .getRewardsByProductId(this.product.id, this.page, this.pageSize)
      .subscribe({
        next: (rewards) => {
          this.rewards = rewards.map((reward) =>
            this.formatRewardForDisplay(reward)
          );
        },
        error: () => {
          this.loading = false;
          this.snackBarService.error(this.labels.error);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }
  /**
   * Converts reward crude data for display in view
   * @param {ProductRewardsResponseDTO} reward to be formated
   * @return {DisplayReward} Formatted Reward
   */
  formatRewardForDisplay(reward: ProductRewardsResponseDTO): DisplayReward {
    const target = reward.discount.toString();
    const targetIndex = reward.name.lastIndexOf(target);
    const displayName = reward.name.substring(0, targetIndex).trim();
    const displayDiscount = reward.name.substring(targetIndex);
    return {
      displayName: displayName,
      displayDiscount: displayDiscount,
      ...reward,
    } as DisplayReward;
  }

  /**
   * Fetch more rewards and append to list
   */
  async pullRewards() {
    if (this.pulling) {
      return;
    }

    this.pulling = true;
    this.page++;

    this.rewardService
      .getRewardsByProductId(this.product.id, this.page, this.pageSize)
      .subscribe((rewards) => {
        if (rewards.length > 0) {
          this.rewards?.push(
            ...rewards.map((reward) => this.formatRewardForDisplay(reward))
          );
        } else {
          this.page = this.page - 1;
        }
      })
      .add(() => {
        this.pulling = false;
      });
  }

  /**
   * Handles image access error on s3
   * @param {ProductRewardsResponseDTO} data
   */
  handleImageError(data: ProductRewardsResponseDTO) {
    data.imgSource = '';
  }
}
